import React, { useCallback, useMemo } from "react";
import { Divider, Flex } from "antd";
import { useTranslation } from "react-i18next";

import { getFieldTypeComponent } from "./editors";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { FieldRendererProps } from "@/domains/datablocks/types";
import BaseFormFieldsList from "@/components_v2/FormFieldsList/BaseFormFieldsList";
import {
  DataFieldResponse,
  DisplayType,
  FieldType,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";

import "../styles.css";

const FieldRenderer = React.memo(
  ({
    field,
    control,
    dataBlockDependencies,
    eitherRequiredRules,
    ref,
  }: FieldRendererProps) => {
    const { t } = useTranslation();

    if (!dataBlockDependencies) return null;

    const { component: FieldComponent, props } = getFieldTypeComponent(
      field as DataFieldResponse,
      dataBlockDependencies,
    );
    const showLabel = field.displayType === DisplayType.CHECKBOX;

    const getBorderStyles = useCallback((value?: number) => {
      if (value === undefined) return "";

      const baseStyles = "either-group-questions";

      switch (value) {
        case -1:
          return baseStyles + " either-group-questions-top";
        case 0:
          return baseStyles;
        case 1:
          return baseStyles + " either-group-questions-bottom";
        default:
          return "";
      }
    }, []);

    const helpText = useMemo(() => {
      if (field.guide) {
        return (
          t("datablocks.labels.getStartedTemplate", {
            guide: field.guide,
          }) +
          " " +
          field.helpText
        );
      }
      return field.helpText;
    }, [field]);

    const isRequired = Boolean(
      (field?.rules || []).find((i) => i.type === ValidationRuleTypes.REQUIRED),
    );

    let borderStyles;
    let showOr;
    if (typeof field.id === "number") {
      const ruleForField = eitherRequiredRules[field.id];
      borderStyles = getBorderStyles(ruleForField);
      showOr = field.id in eitherRequiredRules && ruleForField !== 1;
    }

    return (
      <div>
        <Flex vertical gap={4} className={borderStyles}>
          <FormFieldWrapper
            label={showLabel ? "" : field.title}
            required={isRequired}
            description={field.description ?? undefined}
            helpText={helpText}
          >
            {field.multiple && field.type !== FieldType.SELECT ? (
              <BaseFormFieldsList
                name={field.name}
                control={control}
                headerTitle={field.title.replace("s", "")}
                fields={[
                  {
                    name: "",
                    label: "",
                    component: FieldComponent,
                    props: {
                      control,
                      placeholder: field.placeholder || field.title,
                    },
                  },
                ]}
                defaultValues={""}
                addButtonText={t("datablocks.labels.addNew", {
                  itemName: field.title.replace("s", ""),
                })}
              />
            ) : (
              <FieldComponent
                // @ts-expect-error control is required for any component
                control={control}
                name={field.name}
                placeholder={field.placeholder || field.title}
                ref={ref}
                {...props}
              />
            )}
          </FormFieldWrapper>
        </Flex>

        {showOr && (
          <Divider className="either-group-questions either-group-questions-divider">
            {t("datablocks.labels.or")}
          </Divider>
        )}
      </div>
    );
  },
);

export default FieldRenderer;
